import {DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy, arrayMove} from '@dnd-kit/sortable';
import {Button, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {useState} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import type {TransformedDefinitionApiResponse} from '../../types/Api';
import type {EagColumnState} from '../../types/EagColumnState';
import {getSortModel, type SortModelEntry} from '../../utils/getSortModel';
import {SortableRow} from './SortableRow';

type DraftSortEntry = {columnKey: string; order: 'ASC' | 'DESC'};

type SortDropdownProps = {
  columnStates: EagColumnState[];
  definition: TransformedDefinitionApiResponse;
  onApply: (entries: SortModelEntry[]) => void;
  onClose: () => void;
  /** Cap on number of sort entries (e.g. 1 for SINGLE_SORT). Unlimited when omitted. */
  maxSorts?: number;
} & TestIdProps;

const DRAG_ACTIVATION_DISTANCE = 8;

export function SortDropdown(props: SortDropdownProps) {
  const t = useTranslationContext();
  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: DRAG_ACTIVATION_DISTANCE}})
  );

  // definition.columns is ColumnResponseBody[] (and subtypes) which all have `key`, `title`, `sortable`
  const sortableColumns = props.definition.columns
    .filter((c) => c.sortable)
    .map((c) => ({key: c.key, headerName: c.title}));

  const [draft, setDraft] = useState<DraftSortEntry[]>(() =>
    getSortModel(props.columnStates).map((e) => ({columnKey: e.columnKey, order: e.order}))
  );

  const usedKeys = new Set(draft.map((d) => d.columnKey));

  const getAvailableColumns = (currentKey: string) =>
    sortableColumns.filter((c) => c.key === currentKey || !usedKeys.has(c.key));

  const handleAddSort = () => {
    const next = sortableColumns.find((c) => !usedKeys.has(c.key));
    if (next) {
      setDraft((prev) => [...prev, {columnKey: next.key, order: 'ASC'}]);
    }
  };

  const handleChangeColumn = (index: number, newKey: string) => {
    setDraft((prev) =>
      prev.map((entry, i) => (i === index ? {...entry, columnKey: newKey} : entry))
    );
  };

  const handleChangeOrder = (index: number, order: 'ASC' | 'DESC') => {
    setDraft((prev) => prev.map((entry, i) => (i === index ? {...entry, order} : entry)));
  };

  const handleRemove = (index: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;
    if (!over || active.id === over.id) {
      return;
    }
    setDraft((prev) => {
      const oldIndex = prev.findIndex((e) => e.columnKey === active.id);
      const newIndex = prev.findIndex((e) => e.columnKey === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleApply = () => {
    props.onApply(draft.map((d) => ({columnKey: d.columnKey, order: d.order})));
    props.onClose();
  };

  const handleClear = () => {
    setDraft([]);
  };

  const canAddMore =
    sortableColumns.length > draft.length &&
    (props.maxSorts == null || draft.length < props.maxSorts);

  return (
    <div
      className="min-w-[440px] max-w-[540px] p-5"
      data-testid={suffixTestId('sortDropdown', props)}
    >
      <div className="text-sm font-semibold text-[#091E42] mb-3">
        {t('page.datagrid.labels.sort')}
      </div>

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <SortableContext
          items={draft.map((d) => d.columnKey)}
          strategy={verticalListSortingStrategy}
        >
          {draft.map((entry, index) => (
            <SortableRow
              key={entry.columnKey}
              id={entry.columnKey}
              columnKey={entry.columnKey}
              order={entry.order}
              availableColumns={getAvailableColumns(entry.columnKey)}
              onChangeColumn={(newKey) => handleChangeColumn(index, newKey)}
              onChangeOrder={(order) => handleChangeOrder(index, order)}
              onRemove={() => handleRemove(index)}
              data-testid={props['data-testid']}
            />
          ))}
        </SortableContext>
      </DndContext>

      <Show when={canAddMore}>
        <div className="mt-4">
          <Button
            variant="link"
            size="small"
            onClick={handleAddSort}
            title={t('page.datagrid.button.addSort')}
            data-testid={suffixTestId('sortDropdown-addSort', props)}
          />
        </div>
      </Show>

      <div className="flex justify-end gap-3 mt-4 pt-3" style={{borderTop: '1px solid #EBECF0'}}>
        <Button
          variant="secondary"
          size="default"
          onClick={handleClear}
          title={t('page.datagrid.labels.clear')}
          data-testid={suffixTestId('sortDropdown-clear', props)}
        />
        <Button
          variant="primary"
          size="default"
          onClick={handleApply}
          title={t('page.datagrid.labels.apply')}
          data-testid={suffixTestId('sortDropdown-apply', props)}
        />
      </div>
    </div>
  );
}
